import * as React from "react";
import { Checkbox, ColorPicker, Input } from "uxp/components";
import { FAIcon as FontAwesomeIcon } from '../components/FAIcon';
import jp from "jsonpath";
import { IUIPropertyDefinition } from '../common';
import { Property, PropertyContainer, PropertyContainerTitle, PropertyGroup, PropertyTitle, RemoveButton } from "./common";
import { bindContainerId } from "../../../util";
import classNames from "classnames";
import { $L } from '../../../localize';

interface ColorConfig {
    label: string;
    color: string;
}

interface ColorsPropertyProps {
    colors: ColorConfig[];
    mapLabelsToColors?: boolean;
}

export function extractColorByLabelOrIndex(colorsProperty: ColorsPropertyProps, defaultColors: ColorConfig[], index: number, label?: string): string {
    const { colors, mapLabelsToColors = false } = colorsProperty
    const finalColors = (!!colors && colors?.length > 0) ? colors : defaultColors

    if (mapLabelsToColors) {
        const color = finalColors.find(c => c.label == label)
        return !!color ? color.color : null
    }
    return finalColors?.[index % finalColors.length || 1]?.color || null
}

export function ColorsProperty(title: string, defaultProps: ColorsPropertyProps, fieldId?: string): IUIPropertyDefinition<ColorsPropertyProps> {
    return {
        defaults: defaultProps,
        render: (props, uxpContext, contextData, containerId) => {

            const _props = props.uiProps || { colors: [], mapLabelsToColors: false };
            const mapLabelsToColors = _props.mapLabelsToColors;

            const addColor = () => {
                const _colors = _props.colors || [];
                _colors.unshift({ label: '', color: '' });
                jp.apply(_props, `$.colors`, () => _colors);
                props.onChange(_props);
            };

            const removeColor = (index: number) => {
                const _colors = _props.colors || [];
                _colors.splice(index, 1);
                jp.apply(_props, `$.colors`, () => _colors);
                props.onChange(_props);
            };

            const updateColor = (index: number, field: 'label' | 'color', value: string) => {
                jp.apply(_props, `$.colors[${index}].${field}`, () => value);
                props.onChange(_props);
            };

            return (
                <PropertyContainer>
                    <PropertyContainerTitle>{title || $L('widgetdesigner.props.colors', 'Colors')}</PropertyContainerTitle>

                    <PropertyGroup>
                        <Property>
                            <Checkbox
                                checked={_props?.mapLabelsToColors}
                                onChange={s => {
                                    props.onChange(Object.assign({}, _props, { mapLabelsToColors: s }));
                                }}
                                label={$L('widgetdesigner.props.match-colors-with-labels', 'Match colors with Labels')}
                            />
                        </Property>
                    </PropertyGroup>

                    <PropertyGroup>
                        <div className="wd-ui-property-group-title" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            Colors
                            <div
                                className="plus"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                                onClick={addColor}
                            >
                                <FontAwesomeIcon icon={['fas', 'plus']} />
                            </div>
                        </div>

                        <Property id={bindContainerId(fieldId || 'colors', containerId)}>
                            <PropertyTitle>{$L('widgetdesigner.props.colors', 'Colors')}</PropertyTitle>
                            <div className="wd-colors-property-config-colors">
                                {_props?.colors?.map((item: ColorConfig, i: number) => (
                                    <div key={i}
                                        className={classNames(
                                            "wd-colors-property-config-colors__row",
                                            {
                                                "wd-colors-property-config-colors__row--has-label": mapLabelsToColors
                                            }
                                        )}
                                    >
                                        <div className="wd-colors-property-config-colors__color">
                                            <ColorPicker
                                                color={item.color}
                                                onChange={(color) => updateColor(i, 'color', color)}
                                                position='right'
                                                className='color-input'
                                            />
                                        </div>
                                        {mapLabelsToColors && (
                                            <div className="wd-colors-property-config-colors__label">
                                                <Input
                                                    value={item.label}
                                                    onChange={label => updateColor(i, 'label', label)}
                                                />
                                            </div>
                                        )}
                                        <div className="wd-colors-property-config-colors__deletebtn">
                                            <RemoveButton onClick={() => removeColor(i)} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Property>
                    </PropertyGroup>
                </PropertyContainer>
            );
        }
    };
}